import { reportError } from 'common/src/services/errorLogger';

// 重新导出 reportError 供其他文件使用
export { reportError };

/**
 * 错误类型检查和分类
 */
export const classifyError = (
    error: unknown,
): {
    type: 'network' | 'timeout' | 'validation' | 'unknown';
    isUserFriendly: boolean;
} => {
    if (error instanceof Error) {
        const message = error.message.toLowerCase();
        const name = error.name.toLowerCase();

        if (message.includes('fetch') || message.includes('network') || name.includes('network')) {
            return { type: 'network', isUserFriendly: true };
        }

        if (name.includes('timeout') || message.includes('timeout')) {
            return { type: 'timeout', isUserFriendly: true };
        }

        if (message.includes('validation') || message.includes('invalid')) {
            return { type: 'validation', isUserFriendly: true };
        }
    }

    return { type: 'unknown', isUserFriendly: false };
};

/**
 * 标准化错误对象
 */
export const normalizeError = (error: unknown): Error => {
    if (error instanceof Error) {
        return error;
    }

    if (typeof error === 'string') {
        return new Error(error);
    }

    if (error && typeof error === 'object') {
        return new Error(JSON.stringify(error));
    }

    return new Error('Unknown error occurred');
};

/**
 * API 请求错误处理高阶函数
 * @param fn 需要包装的异步函数
 * @param componentName 组件名称
 * @param description 错误描述
 * @returns 包装后的函数
 */
export const withApiErrorHandling = <TArgs extends any[], TReturn>(
    fn: (...args: TArgs) => Promise<TReturn>,
    componentName: string,
    description: string,
) => {
    return (...args: TArgs): Promise<TReturn> => {
        return fn(...args).catch((error: unknown) => {
            const normalizedError = normalizeError(error);
            const { type } = classifyError(normalizedError);

            reportError({
                error: normalizedError,
                componentName,
                props: args.length > 0 ? { params: args[0] } : undefined,
                additionalInfo: {
                    description,
                    errorType: type,
                    timestamp: new Date().toISOString(),
                },
            });

            throw normalizedError;
        });
    };
};

/**
 * Promise catch 处理器
 * @param error 捕获到的错误
 * @param componentName 组件名称
 * @param props 相关属性
 * @param description 错误描述
 */
export const handlePromiseError = (
    error: unknown,
    componentName: string,
    description: string,
    props?: Record<string, any>,
): never => {
    const normalizedError = normalizeError(error);
    const { type } = classifyError(normalizedError);

    reportError({
        error: normalizedError,
        componentName,
        props,
        additionalInfo: {
            description,
            errorType: type,
            timestamp: new Date().toISOString(),
        },
    });

    throw normalizedError;
};

/**
 * React Hook 错误处理
 * @param error 错误对象
 * @param hookName Hook 名称
 * @param props 相关属性
 */
export const handleHookError = (error: unknown, hookName: string, props?: Record<string, any>): void => {
    const normalizedError = normalizeError(error);
    const { type } = classifyError(normalizedError);

    reportError({
        error: normalizedError,
        componentName: hookName,
        props,
        additionalInfo: {
            description: `${hookName} hook error`,
            errorType: type,
            timestamp: new Date().toISOString(),
            isHookError: true,
        },
    });

    // Hook 错误不抛出，避免影响组件渲染
    console.error(`Hook error in ${hookName}:`, normalizedError);
};
